/**
 * Cumulative Wear Graph Component
 * Displays lifetime wear in compact single-line format with dual-threshold
 * Shows both wear percentage and distance traveled
 */

import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { TrendingUp } from 'lucide-react';

const CumulativeWearGraph = ({ componentHealth, lifespanAlarms, vehicleType, isMetric = true, themeClasses }) => {
  const { theme, getThemeClasses } = useTheme();
  const classes = themeClasses || getThemeClasses();

  // Component display order and abbreviations
  const components = [
    { key: 'engine', abbr: 'Eng', name: 'Engine' },
    { key: 'suspension', abbr: 'Sus', name: 'Suspension' },
    { key: 'brakes', abbr: 'Brk', name: 'Brakes' },
    { key: 'transmission', abbr: 'Trn', name: 'Transmission' }
  ];

  // Get color class based on alarm level
  const getColorClass = (alarmLevel) => {
    switch (alarmLevel) {
      case 3: return 'bg-red-500'; // Critical
      case 2: return 'bg-orange-500'; // Warning
      case 1: return 'bg-yellow-500'; // Caution
      default: return 'bg-green-500'; // Normal
    }
  };

  // Get status icon based on alarm level
  const getStatusIcon = (alarmLevel) => {
    switch (alarmLevel) {
      case 3: return '🔴';
      case 2: return '🔶';
      case 1: return '⚠️';
      default: return '✅';
    }
  };

  // Format distance with abbreviation (k for thousands)
  const formatDistance = (km) => {
    if (!km) return '0';
    
    const distance = isMetric ? km : km * 0.621371; // Convert to miles if needed
    const unit = isMetric ? 'km' : 'mi';
    
    if (distance >= 1000) {
      return `${(distance / 1000).toFixed(0)}k`;
    }
    return `${Math.round(distance)}`;
  };

  // Format full distance for display
  const formatFullDistance = (km, threshold) => {
    const current = formatDistance(km);
    const max = formatDistance(threshold);
    const unit = isMetric ? 'km' : 'mi';
    return `${current}/${max} ${unit}`;
  };

  // Check if data exists - only lifespanAlarms is required
  console.log('🔍 CumulativeWearGraph props:', {
    hasComponentHealth: !!componentHealth,
    hasLifespanAlarms: !!lifespanAlarms,
    componentHealthType: typeof componentHealth,
    lifespanAlarmsType: typeof lifespanAlarms,
    lifespanAlarmsKeys: lifespanAlarms ? Object.keys(lifespanAlarms) : 'null'
  });
  
  if (!lifespanAlarms || Object.keys(lifespanAlarms).length === 0) {
    console.log('❌ CumulativeWearGraph: Missing lifespanAlarms data, showing fallback');
    return (
      <div className={`${classes.secondary} rounded-lg p-4 text-center`}>
        <TrendingUp className={`mx-auto ${classes.textMuted} mb-2`} size={32} />
        <p className={`${classes.textMuted} text-sm`}>
          No cumulative wear data available
        </p>
      </div>
    );
  }
  
  console.log('✅ CumulativeWearGraph: Rendering with data');

  return (
    <div className={`${classes.secondary} rounded-lg p-4`}>
      <h4 className={`text-sm font-semibold ${classes.textPrimary} mb-3 flex items-center gap-2`}>
        <TrendingUp size={16} className="text-purple-400" />
        Cumulative Wear (Component Lifespan)
      </h4>
      
      {/* Single line per component */}
      <div className="space-y-2">
        {components.map(({ key, abbr, name }) => {
          const alarm = lifespanAlarms[key];
          const wear = alarm?.lifetimeWear || 0;
          const distance = alarm?.totalDistanceTraveled || 0;
          const threshold = alarm?.threshold || { wearThreshold: 100, distanceThreshold: 100000 };
          const alarmLevel = alarm?.alarmLevel || 0;
          const triggeredBy = alarm?.triggeredBy || 'wear';
          const wearPercent = threshold.wearThreshold > 0 ? (wear / threshold.wearThreshold) * 100 : 0;

          return (
            <div key={key} className="flex items-center gap-2 text-xs">
              {/* Abbreviated name */}
              <div className={`w-8 font-medium ${classes.textSecondary}`} title={name}>
                {abbr}
              </div>
              
              {/* Progress bar (wear %) */}
              <div className="flex-1 relative">
                <div className={`h-3 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-300'} rounded-full overflow-hidden`}>
                  <div 
                    className={`h-full transition-all duration-300 ${getColorClass(alarmLevel)}`}
                    style={{ width: `${Math.min(wearPercent, 100)}%` }}
                  />
                </div>
              </div>
              
              {/* Compact wear percentage */}
              <div className={`${classes.textPrimary} font-medium w-14 text-right`}>
                {wear.toFixed(1)}/{threshold.wearThreshold}%
              </div>
              
              {/* Status icon */}
              <div className="w-5 text-center">
                {getStatusIcon(alarmLevel)}
              </div>
              
              {/* Distance (inline) */}
              <div className={`${classes.textMuted} w-24 text-right`}>
                {formatFullDistance(distance, threshold.distanceThreshold)}
              </div>
              
              {/* Limiting threshold indicator */}
              <div className="w-5 text-center">
                {triggeredBy === 'distance' && alarmLevel > 0 && (
                  <span className="text-yellow-400" title="Distance is limiting factor">⚠️</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Legend */}
      <div className={`mt-3 pt-3 border-t ${classes.border} text-xs ${classes.textMuted} flex items-center justify-between`}>
        <span>⚠️ = Distance limiting</span>
        <span className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            Normal
          </span>
          <span className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
            Caution
          </span>
          <span className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-orange-500"></div>
            Warning
          </span>
          <span className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-red-500"></div>
            Critical
          </span>
        </span>
      </div>
    </div>
  );
};

export default CumulativeWearGraph;
